import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { AccordionGroup, Button, Checkbox, Icon, Responsive, useDebouncedCallback } from 'rs-emd-ui-atoms';
import { FilterList } from '../..';
import { ILabel } from '../../../../models/common/label.model';
import { IFacet, IFacetValues, IPLPFacets } from '../../../../models/plp/plp-facets.model';
import { OrderedFilter } from '../filters';
import styles from './filter-area-component.module.scss';
import { getLabel } from '../../../../helpers/html.utils';

type FilterAreaProps = {
  labels?: ILabel[] | null;
  filterArea: IPLPFacets;
  selectedFilters: OrderedFilter[];
  hasCategories?: boolean;
  onChangeSelection?: (orderedFilters: OrderedFilter[]) => void;
};

export const FilterArea: FunctionComponent<FilterAreaProps> = (props: FilterAreaProps) => {
  const flagsId = '0';

  const [selected, setSelected] = useState(props.selectedFilters);
  const [openFacet, setOpenFacet] = useState<IFacet | undefined>(undefined);
  const [facetJustClosed, setFacetJustClosed] = useState<IFacet | undefined>(undefined);
  const [facets, setFacets] = useState<IFacet[] | undefined>(props.filterArea.facets ?? undefined);
  const modalRef = useRef<Array<HTMLDivElement | null>>([]);
  const debouncedUpdateSelection = useDebouncedCallback((id: string, keys: (string | number)[]) => updateSelection(id, keys), 1000);

  useEffect(() => {
    setSelected(props.selectedFilters);
  }, [props.selectedFilters]);

  // reset selected facet on clickoutside
  useEffect(() => {
    /**
     * If clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (openFacet) {
        let index = facets?.findIndex((f) => f === openFacet) ?? -1;
        if (
          index > -1 &&
          modalRef &&
          modalRef.current != null &&
          modalRef.current[index] !== event.target &&
          !modalRef.current[index]?.contains(event.target as any)
        ) {
          setFacetJustClosed(openFacet);
          setOpenFacet(undefined);
        }
      }
    }

    if (openFacet) {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef, openFacet, facets]);

  // Navigate through lge-desktop accordions
  useEffect(() => {
    let headerIds = facets ? facets.map((_, index) => 'facet-' + index) : [];

    function onKeyDownFocus(e: any) {
      let id = '';

      for (let i = 0; i < headerIds.length; i++) {
        if (document.activeElement === document.getElementById(headerIds[i])) {
          id = headerIds[i]; //store id of active accordion header
          break;
        }
      }

      //if an accordion header is active
      if (id !== '' && facets) {
        let index = parseInt(id.split('-')[1]);

        //down arrow
        if (e.keyCode === 40) {
          document.getElementById(id)?.blur();

          if (index === facets.length - 1) {
            document.getElementById(headerIds[0])?.focus(); //focus topmost accordion in group
          } else {
            document.getElementById(headerIds[index + 1])?.focus();
          }
        }
        //up arrow
        else if (e.keyCode === 38) {
          document.getElementById(id)?.blur();

          if (index === 0) {
            document.getElementById(headerIds[headerIds.length - 1])?.focus(); //focus last accordion in group
          } else {
            document.getElementById(headerIds[index - 1])?.focus();
          }
        }
        //End key
        else if (e.keyCode === 35) {
          document.getElementById(id)?.blur();
          document.getElementById(headerIds[headerIds.length - 1])?.focus(); //focus last accordion in group
        }
        //Home key
        else if (e.keyCode === 36) {
          document.getElementById(id)?.blur();
          document.getElementById(headerIds[0])?.focus(); //focus topmost accordion in group
        }
      }
    }

    window.addEventListener('keydown', onKeyDownFocus);

    return () => {
      window.removeEventListener('keydown', onKeyDownFocus);
    };
  }, [props.filterArea, facets]);

  //filter out the facets that have zero values when the facets prop changes
  useEffect(() => {
    if (props.filterArea.facets) {
      setFacets(props.filterArea.facets.filter((f) => f.values.length > 0));
    }
  }, [props.filterArea.facets]);

  let accordions = facets?.map((f, index) => ({
    key: f.id,
    className: styles.accordion,
    title: f.displayName,
    isHeaderFixed: true,
    onAccordionToggle: (isOpen: any) => {
      setOpenFacet(isOpen && facets ? facets[index] : undefined);
    },
    children: (
      <FilterList
        labels={props.labels}
        facet={f}
        selectedFilters={props.selectedFilters}
        updateSelection={updateSelection}
        isVisible={f === openFacet}></FilterList>
    ),
  }));

  return (
    <div className={`${styles['filter-area-container']} ${props.hasCategories ? styles.condensed : ''}`}>
      <Checkbox
        inline={false}
        name='flag-facets'
        className={styles['flag-container']}
        selectCallback={(keys) => updateSelection(flagsId, keys)}
        defaultSelection={selected
          .filter((f) => f.id === flagsId)
          .map((f) => {
            return f.filter.id;
          })}
        list={props.filterArea.flagFacets?.map((ff) => {
          return {
            key: ff.id,
            elt: (
              <span className={styles.flag}>
                {ff.id === 'flag_better_world' && <Icon name='sustainable'></Icon>}
                <span>
                  {ff.displayValue} ({ff.resultsCount})
                </span>
              </span>
            ),
            description:
              ff.id === 'flag_better_world' ? (
                <Button
                  href={getLabel(props.labels, 'flag_better_world_link')}
                  text={getLabel(props.labels, 'flag_better_world_description')}
                  buttonType='link'
                  className={styles['flag-description']}></Button>
              ) : undefined,
            isDisabled: ff.disabled ?? false,
          };
        })}
      />
      <Responsive breakpoints={['mobile', 'tablet']}>
        {accordions && <AccordionGroup accordions={accordions} groupName='filter-area-accordion-group' />}
      </Responsive>
      <Responsive breakpoints={['desktop', 'lg-desktop', 'xl-desktop']}>
        {facets?.map((f, index) => {
          return (
            <div className={styles['facet-wrapper']} key={f.id}>
              <div
                id={'facet-' + index}
                className={styles.header}
                tabIndex={0}
                onClick={(e) => {
                  if (facetJustClosed?.id === f.id) setFacetJustClosed(undefined);
                  else toggleFilter(f);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.keyCode === 32 /** Spacebar */) {
                    toggleFilter(f);
                  }
                }}>
                {f.displayName} {<Icon width={20} name={f !== openFacet ? 'chev-right' : 'close'} />}
              </div>
              <div
                className={`${styles.modal} ${openFacet === f ? styles.show : styles.hide}`}
                ref={(el) => (modalRef.current[index] = el)}>
                {openFacet === f && (
                  <>
                    <div className={styles.header}>{openFacet.displayName}</div>
                    <div className={styles.options}>
                      <FilterList
                        labels={props.labels}
                        facet={openFacet}
                        selectedFilters={props.selectedFilters}
                        updateSelection={debouncedUpdateSelection}
                        isVisible={true}></FilterList>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Responsive>
    </div>
  );

  function toggleFilter(facet: IFacet | undefined) {
    if ((facet?.values.length ?? 0) > 0)
      setOpenFacet((curr) => {
        if (curr?.id === facet?.id) return undefined;
        else return facet;
      });
  }

  function updateSelection(id: string, keys: (string | number)[]) {
    let ordered = selected;
    let currCategory = ordered.filter((o) => o.id === id);

    // remove flag filters
    if (id === flagsId) {
      ordered = ordered.filter((u) => {
        var isFlagFacet = (props.filterArea.flagFacets ?? [])?.filter((ff) => ff.id === u.id).length > 0;
        return (isFlagFacet && keys.includes(u.id)) || !isFlagFacet;
      });
    }

    //  was some other filter removed?
    if (currCategory.length > keys.length) {
      ordered = ordered.filter(
        (u) =>
          (u.id === id && keys.includes(u.filter.id)) || // interesection
          u.id !== id
      );
    } else if (currCategory.length < keys.length || id === flagsId) {
      var newfilters: IFacetValues[] | undefined = [];
      var newfilterCategory: IFacet | undefined = undefined;

      // add flag filters?
      if (id === flagsId && props.filterArea.flagFacets) {
        newfilters = props.filterArea.flagFacets.filter((f) => keys.includes(f.id));
      } else {
        newfilterCategory = facets?.filter((c) => c.id === id)[0];
        newfilters = newfilterCategory?.values.filter((f) => keys.includes(f.id));
      }

      if (newfilters)
        newfilters.forEach((element) => {
          if (!ordered.some((f) => f.id === newfilterCategory?.id && f.filter.id === element.id))
            ordered.unshift({
              id: newfilterCategory?.id ?? flagsId,
              name: newfilterCategory?.displayName ?? '',
              filter: element,
            });
        });
    }

    props.onChangeSelection && props.onChangeSelection(ordered);
  }
};
